import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Outlet, useParams } from "react-router-dom";
import {
  getInvite,
  InviteSummary,
  calculateMyQuote,
  QuoteAnswers,
  QuoteResult,
} from "../../api/services";

interface SDAContextValue {
  inviteUuid: string;
  invite: InviteSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  // Underwriting answers — set by SDAUnderwriting, consumed by SDAQuote.
  answers: QuoteAnswers | null;
  setAnswers: (a: QuoteAnswers) => void;

  // Calculated quote — fetched from /quote.
  quote: QuoteResult | null;
  quoteLoading: boolean;
  quoteError: string | null;
  quoteAttempted: boolean;
  loadQuote: () => Promise<QuoteResult | null>;
}

const SDAContext = createContext<SDAContextValue | undefined>(undefined);

/**
 * Loads the invite once per SDA flow visit and shares it with every child
 * route under `/sda/:uuid/*`. Also holds the underwriting answers and the
 * resulting quote so steps don't need to re-ask or re-compute.
 */
export const SDAProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { uuid } = useParams<{ uuid: string }>();
  const [invite, setInvite] = useState<InviteSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [answers, setAnswersState] = useState<QuoteAnswers | null>(null);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  // We've kicked off `loadQuote` at least once for this invite. Used to
  // prevent the auto-load effect from looping on failure (loading→false,
  // condition becomes true again, fires forever).
  const [quoteAttempted, setQuoteAttempted] = useState(false);

  const refresh = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getInvite(uuid);
      setInvite(res.data);
    } catch (err) {
      console.error("Failed to load SDA invite:", err);
      setError("Failed to load invite");
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Updating answers invalidates any previously calculated quote.
  const setAnswers = useCallback((a: QuoteAnswers) => {
    setAnswersState(a);
    setQuote(null);
    setQuoteError(null);
    setQuoteAttempted(false);
  }, []);

  const loadQuote = useCallback(async (): Promise<QuoteResult | null> => {
    if (!uuid) return null;
    // If the invite already has a persisted quote the server ignores answers
    // and returns the saved row, so we just hit /quote with a stub payload.
    const hasPersistedQuote = !!invite?.quoteId;
    if (!answers && !hasPersistedQuote) {
      setQuoteError("Please answer the underwriting questions first.");
      return null;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    setQuoteAttempted(true);
    try {
      const payload = answers ?? { usResident: true };
      const res = await calculateMyQuote(uuid, payload);
      setQuote(res.data);
      return res.data;
    } catch (err: any) {
      console.error("Failed to calculate quote:", err);
      setQuoteError(
        err?.response?.data?.message || "Failed to calculate quote",
      );
      return null;
    } finally {
      setQuoteLoading(false);
    }
  }, [uuid, answers, invite?.quoteId]);

  // Auto-load the persisted quote once we know one exists on the invite.
  // Gated on `quoteAttempted` so a failed call doesn't retry in a loop:
  // when loadQuote fails, quoteLoading flips back to false but
  // quoteAttempted stays true, breaking the cycle. The user can still
  // retry manually via the button on SDAQuote.
  useEffect(() => {
    if (invite?.quoteId && !quote && !quoteLoading && !quoteAttempted) {
      loadQuote();
    }
  }, [invite?.quoteId, quote, quoteLoading, quoteAttempted, loadQuote]);

  const value: SDAContextValue = {
    inviteUuid: uuid ?? "",
    invite,
    loading,
    error,
    refresh,
    answers,
    setAnswers,
    quote,
    quoteLoading,
    quoteError,
    quoteAttempted,
    loadQuote,
  };

  return (
    <SDAContext.Provider value={value}>
      {children ?? <Outlet />}
    </SDAContext.Provider>
  );
};

export function useSDAInvite(): SDAContextValue {
  const ctx = useContext(SDAContext);
  if (!ctx) {
    throw new Error("useSDAInvite must be used inside an <SDAProvider>");
  }
  return ctx;
}
